/**
 * useItemStatus.js - Refactored for optimal Vue 3 composable patterns
 *
 * Key improvements:
 * - Uses toValue() instead of unref() for better flexibility (supports getters)
 * - Simplified reactivity - no unnecessary wrapping
 * - Removed redundant computeds where simple object access suffices
 * - Better performance with fewer reactive layers
 */

import { computed, toValue } from 'vue';
import { useI18n } from 'vue-i18n';
import { ItemStatus } from '@/enums/commerce/ItemStatus.js';
import {
  ItemStatusCategories,
  ItemStatusMeta,
  itemStatusLabelKey,
  ItemStatusTransitions,
} from '@/enums/commerce/ItemStatusConfig.js';
import { boxCanTakeItBack } from '@/utils/supplierSuggestion.js';

/**
 * @param {Ref<Object>|Object|Function} item - Item object (can be ref, reactive, getter, or plain object)
 * @param {Ref<Object>|Object|Function} [box] - the item's OrderBox, or an
 *   equivalent `{ status, deliveredConfirmedAt }`. OPTIONAL and backward
 *   compatible: only `canCancel` reads it, and only on an OUT_OF_STOCK line,
 *   where it decides whether the carton can still take the item back. A caller
 *   that omits it gets exactly the pre-2026-09-15 answers.
 */
export function useItemStatus(item, box) {
  const { t } = useI18n();

  // ===========================================================================
  // REACTIVE STATUS - Use toValue for maximum flexibility
  // ===========================================================================
  // Coerced to a Number on the way in. Every category check below is
  // `Array.prototype.includes`, which is strict — but ItemStatusMeta lookups are
  // object-key access, which coerces. A string `"1"` off the wire therefore used
  // to render a correct chip while silently answering false to every gate, i.e. a
  // genuinely PENDING line the member could not edit or cancel and no error to
  // explain it. classifyItemIssue already guards this the same way
  // (useItemIssuePresentation.js). `?? PENDING` runs first so an absent status
  // still defaults rather than becoming NaN.
  const currentStatus = computed(() => {
    const raw = toValue(item)?.status ?? ItemStatus.PENDING;
    return typeof raw === 'string' ? Number(raw) : raw;
  });

  const existingId = computed(() => toValue(item)?.existingId);

  // ===========================================================================
  // METADATA - Direct access, no need for intermediate computeds
  // ===========================================================================
  const meta = computed(() => ItemStatusMeta[currentStatus.value] || {});

  /**
   * WHY a terminal line ended this way, when the backend says so. Additive and
   * OMITTED when the line carries no reason — so an absent key and an explicit
   * null read the same, exactly like `availability` and `supplierSuggestion`.
   */
  const lossReason = computed(() => toValue(item)?.lossReason ?? null);

  /**
   * The i18n prefix, which is NOT always `meta.i18nKey`: a CANCELLED line
   * closed as "not what you ordered" reads under its own key instead. The rule
   * lives beside the CANCELLED meta it overrides — see `itemStatusLabelKey`.
   *
   * Routing BOTH label and description through it is what makes the override
   * reach every surface at once: the row chip, the details sheet chip and the
   * timeline entry all read from here, so none of them can drift into calling
   * the line «ملغي» while the others do not.
   */
  const labelKey = computed(() => itemStatusLabelKey(currentStatus.value, lossReason.value));

  const label = computed(() =>
    labelKey.value ? t(`${labelKey.value}.label`) : '',
  );

  const description = computed(() =>
    labelKey.value ? t(`${labelKey.value}.description`) : '',
  );

  const color = computed(() => meta.value.color || 'gray');
  const icon = computed(() => meta.value.icon || 'circle');

  // ===========================================================================
  // TRANSITION VALIDATION
  // ===========================================================================
  const allowedTransitions = computed(() =>
    ItemStatusTransitions[currentStatus.value] || [],
  );

  const canTransitionTo = (newStatus) =>
    allowedTransitions.value.includes(newStatus);

  const allowedTransitionsWithMeta = computed(() =>
    allowedTransitions.value.map(status => {
      const statusMeta = ItemStatusMeta[status];
      return {
        value: status,
        label: statusMeta?.i18nKey ? t(`${statusMeta.i18nKey}.label`) : '',
        description: statusMeta?.i18nKey ? t(`${statusMeta.i18nKey}.description`) : '',
        color: statusMeta?.color || 'gray',
        icon: statusMeta?.icon || 'circle',
      };
    }),
  );

  // ===========================================================================
  // STATUS CATEGORY CHECKS - Simple, direct checks
  // ===========================================================================
  const isActive = computed(() =>
    ItemStatusCategories.ACTIVE.includes(currentStatus.value),
  );

  const isInTransit = computed(() =>
    ItemStatusCategories.IN_TRANSIT.includes(currentStatus.value),
  );

  const needsAttention = computed(() =>
    ItemStatusCategories.NEEDS_ATTENTION.includes(currentStatus.value),
  );

  const isCompleted = computed(() =>
    ItemStatusCategories.COMPLETED.includes(currentStatus.value),
  );

  const isFailed = computed(() =>
    ItemStatusCategories.FAILED.includes(currentStatus.value),
  );

  const isTerminal = computed(() =>
    ItemStatusCategories.TERMINAL.includes(currentStatus.value),
  );

  const isEditable = computed(() =>
    ItemStatusCategories.EDITABLE.includes(currentStatus.value),
  );

  const isCancelled = computed(() =>
    currentStatus.value === ItemStatus.CANCELLED,
  );

  const isRestorable = computed(() =>
    ItemStatusCategories.RESTORABLE.includes(currentStatus.value),
  );

  // ===========================================================================
  // FORM STATE HELPERS - Combined logic for better performance
  // ===========================================================================
  const isReadOnly = computed(() =>
    !isEditable.value || isTerminal.value || isCancelled.value,
  );

  /**
     * True for ISSUE and OUT_OF_STOCK statuses.
     *
     * "The member can still act on this line", NOT "blocks shipping". The old
     * change-supplier / wait-for-restock / cancel resolution sheet this once
     * gated is gone; today it marks the line as carrying an issue the member can
     * open — the decisions-inbox entry, the issue detail route, and the issue
     * step in the item timeline. OUT_OF_STOCK belongs here (the member may still
     * read what happened) but must never gate shipping. Ship-blocking is the
     * separate BLOCKS_SHIPPING category (= ISSUE only).
     */
  const isIssue = computed(() =>
    ItemStatusCategories.RESOLUTION_ACTIONABLE.includes(currentStatus.value),
  );

  /**
     * OWNER-LOCKED 2026-08-09 — no member edits during COLLECTING. A collector is
     * physically shopping for the item; a change underneath them races the real
     * world.
     *
     * All four field gates read the ONE narrow category rather than deriving from
     * `!isReadOnly`. That derivation happened to give the right answer only after
     * COLLECTING was removed from EDITABLE — it permitted every edit on
     * COLLECTING before that, and it would silently permit them again the moment
     * anyone widened EDITABLE or isReadOnly for an unrelated reason. Reading
     * QUANTITY_EDITABLE directly makes the gate intentional instead of incidental.
     *
     * NOTE this protects the FIELDS only. `EDITABLE` still independently drives
     * `isEditable` → `canDelete`, `validateAction('edit')`, and the whole edit
     * block in ItemSheets.vue (`v-if="canEdit"` → `itemStatus.isEditable`). Widening
     * EDITABLE would therefore re-open the edit SHEET on COLLECTING even though
     * these four gates stayed shut — a sheet of disabled inputs, not an edit, but
     * still wrong. Both halves are pinned in
     * tests/unit/composables/useItemStatus.memberGates.test.js.
     */
  const isFieldEditable = computed(() =>
    ItemStatusCategories.QUANTITY_EDITABLE.includes(currentStatus.value),
  );

  const canEditQuantity = isFieldEditable;
  const canEditNotes = isFieldEditable;
  const canEditAvatar = isFieldEditable;
  const canEditReservation = isFieldEditable;

  /**
     * Mirrors OrderBoxService::memberCancelItem's source-status gate — PENDING
     * only. Was `canTransitionTo(CANCELLED)`, which reads a transition table
     * covering admin-driven edges too and so said yes on ISSUE,
     * AWAITING_APPROVAL and COLLECTED; for a line not in an active plan the
     * backend answers those with a 422 carrying an English exception sentence
     * that the editor surfaces verbatim.
     *
     * The SECOND refusal — a PENDING line a collector is already out buying — is
     * a 403 from OrderBoxItemPolicy::cancel, and the PWA could not predict it
     * until 2026-09-07: `isInActivePlan()` had no field on the resource. It has
     * one now (`inActivePlan`, brief 2026-09-07-member-contract-round-two),
     * reading the same predicate the write lock uses, so the control is withheld
     * up front instead of being offered until the first refusal. Tested for
     * PRESENCE — the key is `true` or omitted, never `false`.
     *
     * The 403 handling downstream stays exactly as it is: a line whose fact has
     * not been re-read since the collector picked it up still refuses, and
     * `planLockedItemIds` still disables the control from then on.
     */
  const canCancel = computed(() => {
    if (!ItemStatusCategories.MEMBER_CANCELLABLE.includes(currentStatus.value)) return false;
    if (toValue(item)?.inActivePlan === true) return false;

    // THE THIRD GATE, and it applies to ONE status. A PENDING line is
    // cancellable wherever it is; an OUT_OF_STOCK one is cancellable only while
    // its carton could still take the item back, because that window is
    // precisely when her money is still being held for it.
    //
    // FAILS CLOSED, and that is the backward-compatible direction: a host that
    // passes no carton gets exactly the answer this gave before 2026-09-15 —
    // PENDING only. Offering Cancel on an out-of-stock line without knowing the
    // carton would be a 422 in Arabic on a line she cannot fix, which is the
    // failure this whole gate exists to avoid.
    if (currentStatus.value === ItemStatus.OUT_OF_STOCK) {
      return boxCanTakeItBack(toValue(box));
    }

    return true;
  });

  const canRestore = computed(() =>
    isRestorable.value && canTransitionTo(ItemStatus.PENDING),
  );

  const canDelete = computed(() =>
    !existingId.value && isEditable.value,
  );

  // ===========================================================================
  // ACTION VALIDATORS
  // ===========================================================================
  const validateAction = (action, payload = {}) => {
    switch (action) {
      case 'cancel':
        return canCancel.value
          ? { valid: true }
          : { valid: false, error: t('item.status.errors.cannotCancel') };

      case 'restore':
        return canRestore.value
          ? { valid: true }
          : { valid: false, error: t('item.status.errors.cannotRestore') };

      case 'edit':
        return !isReadOnly.value
          ? { valid: true }
          : { valid: false, error: t('item.status.errors.cannotEdit') };

      case 'changeQuantity':
        if (!canEditQuantity.value) {
          return { valid: false, error: t('item.status.errors.cannotChangeQuantity') };
        }
        if (payload.qty <= 0) {
          return { valid: false, error: t('item.status.errors.quantityMustBePositive') };
        }
        return { valid: true };

      case 'changeReservation':
        if (!canEditReservation.value) {
          return { valid: false, error: t('item.status.errors.cannotChangeReservation') };
        }
        if (payload.reservationStatus !== 'none' && !payload.reservedForName?.trim()) {
          return { valid: false, error: t('item.status.errors.reservedNameRequired') };
        }
        if (payload.reservedForName && payload.reservedForName.trim().length < 5) {
          return { valid: false, error: t('item.status.errors.reservedNameMinLength') };
        }
        return { valid: true };

      case 'transitionTo': {
        const newStatus = payload.status;
        if (!canTransitionTo(newStatus)) {
          const newStatusLabel = ItemStatusMeta[newStatus]?.i18nKey
            ? t(`${ItemStatusMeta[newStatus].i18nKey}.label`)
            : t('item.status.errors.unknownStatus');

          return {
            valid: false,
            error: t('item.status.errors.invalidTransition', {
              from: label.value,
              to: newStatusLabel,
            }),
          };
        }
        return { valid: true };
      }

      default:
        return { valid: true };
    }
  };

  // ===========================================================================
  // UI HELPERS - Memoized functions for better performance
  // ===========================================================================
  const badgeConfig = computed(() => ({
    text: label.value,
    color: color.value,
    icon: icon.value,
  }));

  const getActionButton = () => {
    if (canRestore.value) {
      return {
        type: 'restore',
        label: t('item.status.actions.restore'),
        icon: 'arrow_clockwise_circle_fill',
        color: 'green',
        enabled: true,
      };
    }

    if (canCancel.value) {
      const isNew = !existingId.value;
      return {
        type: isNew ? 'delete' : 'cancel',
        label: isNew ? t('item.status.actions.delete') : t('item.status.actions.cancel'),
        icon: isNew ? 'trash_fill' : 'xmark_circle_fill',
        color: isNew ? 'red' : 'orange',
        enabled: true,
      };
    }

    return {
      type: 'none',
      label: '',
      icon: '',
      color: 'gray',
      enabled: false,
    };
  };

  const getStatusBadge = () => {
    if (isCancelled.value) {
      return {
        type: 'cancelled',
        label: t('item.status.badges.cancelled'),
        icon: 'xmark_circle_fill',
        color: 'red',
      };
    }

    if (isReadOnly.value && !isTerminal.value) {
      return {
        type: 'readonly',
        label: t('item.status.badges.readonly'),
        icon: 'lock_fill',
        color: 'gray',
      };
    }

    return null;
  };

  // ===========================================================================
  // RETURN API - Only what's needed
  // ===========================================================================
  return {
    // Constants
    ItemStatus,

    // Current Status
    currentStatus,

    // Metadata
    label,
    description,
    lossReason,
    color,
    icon,
    meta,

    // Transitions
    canTransitionTo,
    allowedTransitions,
    allowedTransitionsWithMeta,

    // Category Checks
    isActive,
    isInTransit,
    needsAttention,
    isCompleted,
    isFailed,
    isTerminal,
    isEditable,
    isCancelled,
    isRestorable,
    isIssue,

    // Form State
    canEditQuantity,
    canEditNotes,
    canEditAvatar,
    canEditReservation,
    canCancel,
    canRestore,
    canDelete,
    isReadOnly,

    // Validators
    validateAction,

    // UI Helpers
    badgeConfig,
    getActionButton,
    getStatusBadge,
  };
}

/**
 * Standalone helpers (no item context needed)
 */
export const ItemStatusHelpers = {
  getLabel: (status, t) => {
    const meta = ItemStatusMeta[status];
    return meta?.i18nKey ? t(`${meta.i18nKey}.label`) : '';
  },

  getColor: (status) => ItemStatusMeta[status]?.color || 'gray',

  getIcon: (status) => ItemStatusMeta[status]?.icon || 'circle',

  isInCategory: (status, category) =>
    ItemStatusCategories[category]?.includes(status) || false,

  getByCategory: (category) =>
    ItemStatusCategories[category] || [],
};
